/**
 * Личные настройки пользователя — хранятся на сервере и применяются поверх общих (настроек администратора).
 * Касаются только того, как пользователь видит трекер: вид доски, свой порядок и видимость колонок,
 * сортировка карточек, уведомления и звук, свёрнутое меню. На раскладку запросов и права не влияют.
 *
 * Любое поле может отсутствовать или устареть — схема подставляет значения по умолчанию,
 * неизвестные ключи колонок (удалённые колонки) просто игнорируются при показе.
 */
import { z } from 'zod';

const columnKey = z.string().trim().min(1).max(100);

export const BOARD_LAYOUTS = ['row', 'grid', 'compact'] as const;
export type BoardLayout = (typeof BOARD_LAYOUTS)[number];

export const NOTIFY_SOUND_KINDS = ['pik', 'drop', 'chime'] as const;

const boardPrefsSchema = z.object({
  /** Вид доски: колонки в ряд, сетка, компактная сетка. */
  layout: z.enum(BOARD_LAYOUTS).catch('row').default('row'),
  /** Свой порядок колонок (перетаскиванием). Пусто — общий порядок из настроек. */
  columnOrder: z.array(columnKey).max(300).catch([]).default([]),
  /** Скрытые колонки (галочки «Колонки» над доской). Храним скрытые — новые колонки видны сразу. */
  hidden: z.array(columnKey).max(300).catch([]).default([]),
  /** Порядок карточек в колонке: старые сверху (по умолчанию) или новые. */
  sort: z.record(columnKey, z.enum(['old', 'new'])).catch({}).default({}),
  /** Показывать недавно закрытые. */
  showClosed: z.boolean().catch(true).default(true),
  /** Панель фильтров над доской свёрнута (переключатель у заголовка «Доска»). */
  filtersCollapsed: z.boolean().catch(false).default(false),
  /** Вкладки по группам колонок (Трейдинг, Учет, …): на доске только колонки выбранной группы. */
  groupTabs: z.boolean().catch(false).default(false),
  /** Выбранная вкладка: `g<id>` группы или `other` — колонки вне групп. */
  groupTab: z.string().max(40).nullable().catch(null).default(null),
});

/** События доски для уведомлений: новый запрос, «горит», скоро «Брошен», сорвано обещание. */
const notifyEventsSchema = z.object({
  newTicket: z.boolean().catch(true).default(true),
  sla: z.boolean().catch(true).default(true),
  nearAbandon: z.boolean().catch(false).default(false),
  promiseBroken: z.boolean().catch(false).default(false),
});

const notifyPrefsSchema = z.object({
  enabled: z.boolean().catch(false).default(false),
  events: notifyEventsSchema.catch(() => notifyEventsSchema.parse({})).default({}),
  sound: z.boolean().catch(true).default(true),
  soundKind: z.enum(NOTIFY_SOUND_KINDS).catch('pik').default('pik'),
  volume: z.number().int().min(0).max(100).catch(40).default(40),
  soundWhenFocused: z.boolean().catch(false).default(false),
  /** Колонки для уведомлений; пусто — все. */
  columns: z.array(columnKey).max(300).catch([]).default([]),
});

const uiPrefsSchema = z.object({
  sidebarCollapsed: z.boolean().catch(false).default(false),
  /** Аналитика: блок плиток показателей свёрнут в одну строку. */
  analyticsKpisCollapsed: z.boolean().catch(false).default(false),
});

export const userPrefsSchema = z.object({
  board: boardPrefsSchema.catch(() => boardPrefsSchema.parse({})).default({}),
  notify: notifyPrefsSchema.catch(() => notifyPrefsSchema.parse({})).default({}),
  ui: uiPrefsSchema.catch(() => uiPrefsSchema.parse({})).default({}),
});
export type UserPrefs = z.infer<typeof userPrefsSchema>;
export type BoardPrefs = UserPrefs['board'];
export type NotifyPrefs = UserPrefs['notify'];

export const defaultUserPrefs = (): UserPrefs => userPrefsSchema.parse({});

/**
 * Порядок колонок: свой порядок пользователя поверх общего. Колонки из своего порядка идут как он их расставил;
 * колонки, которых в нём нет (новые), встают на своё общее место — сразу за ближайшей предыдущей по общему порядку.
 */
export function orderColumnKeys(globalKeys: readonly string[], personal: readonly string[]): string[] {
  const known = new Set(globalKeys);
  const result = [...new Set(personal)].filter((k) => known.has(k));
  if (!result.length) return [...globalKeys];
  const placed = new Set(result);
  globalKeys.forEach((key, i) => {
    if (placed.has(key)) return;
    // ближайшая предыдущая по общему порядку колонка, уже стоящая в результате
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const idx = result.indexOf(globalKeys[j]!);
      if (idx >= 0) {
        at = idx + 1;
        break;
      }
    }
    result.splice(at, 0, key);
    placed.add(key);
  });
  return result;
}

/** Перенос колонки `key` на место `target` (до или после неё) — новый личный порядок. */
export function moveColumnKey(order: readonly string[], key: string, target: string, place: 'before' | 'after'): string[] {
  if (key === target) return [...order];
  const without = order.filter((k) => k !== key);
  const idx = without.indexOf(target);
  if (idx < 0) return [...order];
  without.splice(place === 'before' ? idx : idx + 1, 0, key);
  return without;
}
